import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../context/AuthContext';
import {
  getAdminComps,
  AdminCompsResponse,
  AdminCompListing,
} from '../../lib/backendApi';
interface AdminCompsSectionProps {
  variantId: string;
}

const SOURCE_COLORS: Record<string, string> = {
  ebay: '#0064D2',
  vinted: '#09B1BA',
  grailed: '#000000',
};

// Marketplace conditions after normalize_condition(): New, Like New, Good, Used, Fair
const MARKETPLACE_CONDITION_COLORS: Record<string, string> = {
  'new': '#22c55e',
  'like new': '#22c55e',
  'good': '#ca8a04',
  'used': '#f97316',
  'fair': '#dc2626',
};

function getMarketplaceConditionColor(condition: string): string {
  return MARKETPLACE_CONDITION_COLORS[condition.toLowerCase()] || '#9E9E9E';
}

export function AdminCompsSection({ variantId }: AdminCompsSectionProps) {
  const { session } = useAuth();
  const [comps, setComps] = useState<AdminCompsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadComps();
  }, [variantId, session?.access_token]);

  const loadComps = async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getAdminComps(session.access_token, variantId);
      setComps(data);
    } catch (err) {
      console.error('Failed to load comps:', err);
      setError(err instanceof Error ? err.message : 'Failed to load comps');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerText}>Comps</Text>
          <ActivityIndicator size="small" color="#000" />
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerText}>Comps</Text>
          <Text style={styles.errorText}>{error}</Text>
        </View>
        <TouchableOpacity style={styles.retryButton} onPress={loadComps}>
          <Text style={styles.retryText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!comps || comps.total_count === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerText}>Comps</Text>
          <Text style={styles.emptyText}>No comps available</Text>
        </View>
      </View>
    );
  }

  const pricedCount = comps.listings.filter((l) => l.used_in_pricing).length;

  const summaryText = comps.summaries
    .map((s) => `${s.count} ${s.source}`)
    .join(', ');

  // Group listings by source
  const listingsBySource: Record<string, AdminCompListing[]> = {};
  for (const listing of comps.listings) {
    const source = listing.source;
    if (!listingsBySource[source]) {
      listingsBySource[source] = [];
    }
    listingsBySource[source].push(listing);
  }

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={styles.header}
        onPress={() => setExpanded(!expanded)}
      >
        <View style={styles.headerLeft}>
          <Text style={styles.headerText}>
            Comps ({comps.total_count} total{pricedCount > 0 ? `, ${pricedCount} priced` : ''})
          </Text>
          <Text style={styles.summaryText}>{summaryText}</Text>
        </View>
        <View style={styles.headerRight}>
          {comps.snapshot_date && (
            <Text style={styles.dateText}>{comps.snapshot_date}</Text>
          )}
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={18}
            color="#000"
          />
        </View>
      </TouchableOpacity>

      {expanded && (
        <View style={styles.body}>
          {/* Pricing filter banner */}
          {comps.pricing_filter && (
            <View style={styles.pricingBanner}>
              <Text style={styles.pricingBannerText}>
                Pricing: {comps.pricing_filter.condition}
                {comps.pricing_filter.confidence_threshold != null
                  ? ` | confidence >= ${comps.pricing_filter.confidence_threshold}`
                  : ''}
                {comps.pricing_filter.price_min != null && comps.pricing_filter.price_max != null
                  ? ` | $${comps.pricing_filter.price_min.toFixed(0)}–$${comps.pricing_filter.price_max.toFixed(0)}`
                  : ''}
              </Text>
              <Text style={styles.pricingBannerCount}>
                {comps.pricing_filter.included_count} listings used
              </Text>
            </View>
          )}

          {/* Source summaries */}
          <View style={styles.summaryRow}>
            {comps.summaries.map((s) => (
              <View key={s.source} style={styles.summaryCard}>
                <View
                  style={[
                    styles.sourceBadge,
                    { backgroundColor: SOURCE_COLORS[s.source] || '#6b7280' },
                  ]}
                >
                  <Text style={styles.sourceBadgeText}>
                    {s.source.toUpperCase()}
                  </Text>
                </View>
                <Text style={styles.summaryCount}>{s.count} listings</Text>
                {s.median_price != null && (
                  <Text style={styles.summaryMedian}>
                    Median: ${s.median_price.toFixed(0)}
                  </Text>
                )}
              </View>
            ))}
          </View>

          {/* Listings grouped by source */}
          {Object.entries(listingsBySource).map(([source, listings]) => (
            <View key={source} style={styles.sourceSection}>
              <View style={styles.sourceSectionHeader}>
                <View
                  style={[
                    styles.sourceBadgeSmall,
                    { backgroundColor: SOURCE_COLORS[source] || '#6b7280' },
                  ]}
                >
                  <Text style={styles.sourceBadgeTextSmall}>
                    {source.toUpperCase()}
                  </Text>
                </View>
                <Text style={styles.sourceSectionCount}>
                  {listings.length} listings
                  {listings.some((l) => l.used_in_pricing) &&
                    ` (${listings.filter((l) => l.used_in_pricing).length} priced)`}
                </Text>
              </View>

              {listings.map((listing, index) => (
                <TouchableOpacity
                  key={`${source}-${index}-${listing.price}`}
                  style={[
                    styles.compCard,
                    !listing.used_in_pricing && { opacity: 0.35 },
                  ]}
                  onPress={() => {
                    if (listing.item_web_url) {
                      Linking.openURL(listing.item_web_url).catch(() => {});
                    }
                  }}
                  disabled={!listing.item_web_url}
                >
                  <View style={styles.compCardContent}>
                    <Text style={styles.compTitle} numberOfLines={1}>
                      {listing.title || 'Untitled'}
                    </Text>
                    <View style={styles.compMeta}>
                      <Text style={styles.compPrice}>
                        ${listing.price.toFixed(0)}
                      </Text>
                      {listing.condition && (
                        <View
                          style={[
                            styles.conditionBadge,
                            { backgroundColor: getMarketplaceConditionColor(listing.condition) },
                          ]}
                        >
                          <Text style={styles.conditionText}>
                            {listing.condition}
                          </Text>
                        </View>
                      )}
                      {listing.item_web_url && (
                        <Ionicons
                          name="open-outline"
                          size={14}
                          color="#6b7280"
                        />
                      )}
                    </View>
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 16,
    borderWidth: 2,
    borderColor: '#000',
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerText: {
    fontSize: 14,
    fontWeight: 'bold',
    fontFamily: 'Courier',
    color: '#000',
  },
  summaryText: {
    fontSize: 12,
    fontFamily: 'Courier',
    color: '#6b7280',
    marginTop: 2,
  },
  dateText: {
    fontSize: 11,
    fontFamily: 'Courier',
    color: '#6b7280',
  },
  emptyText: {
    fontSize: 12,
    fontFamily: 'Courier',
    color: '#6b7280',
  },
  errorText: {
    fontSize: 12,
    fontFamily: 'Courier',
    color: '#dc2626',
  },
  retryButton: {
    padding: 8,
    alignItems: 'center',
  },
  retryText: {
    fontSize: 12,
    fontFamily: 'Courier',
    fontWeight: '600',
    color: '#000',
    textDecorationLine: 'underline',
  },
  body: {
    padding: 12,
  },
  pricingBanner: {
    backgroundColor: '#f0fdf4',
    borderWidth: 1,
    borderColor: '#bbf7d0',
    padding: 8,
    marginBottom: 12,
  },
  pricingBannerText: {
    fontSize: 11,
    fontFamily: 'Courier',
    fontWeight: '600',
    color: '#166534',
  },
  pricingBannerCount: {
    fontSize: 10,
    fontFamily: 'Courier',
    color: '#166534',
    marginTop: 2,
  },
  summaryRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  summaryCard: {
    flex: 1,
    alignItems: 'center',
    padding: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  sourceBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    marginBottom: 6,
  },
  sourceBadgeText: {
    fontSize: 10,
    fontWeight: 'bold',
    fontFamily: 'Courier',
    color: '#fff',
  },
  summaryCount: {
    fontSize: 12,
    fontFamily: 'Courier',
    color: '#000',
  },
  summaryMedian: {
    fontSize: 11,
    fontFamily: 'Courier',
    color: '#6b7280',
    marginTop: 2,
  },
  sourceSection: {
    marginBottom: 12,
  },
  sourceSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  sourceBadgeSmall: {
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  sourceBadgeTextSmall: {
    fontSize: 9,
    fontWeight: 'bold',
    fontFamily: 'Courier',
    color: '#fff',
  },
  sourceSectionCount: {
    fontSize: 11,
    fontFamily: 'Courier',
    color: '#6b7280',
  },
  compCard: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  compCardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  compTitle: {
    flex: 1,
    fontSize: 12,
    fontFamily: 'Courier',
    color: '#000',
    marginRight: 8,
  },
  compMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  compPrice: {
    fontSize: 12,
    fontWeight: 'bold',
    fontFamily: 'Courier',
    color: '#000',
  },
  conditionBadge: {
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  conditionText: {
    fontSize: 9,
    fontWeight: '600',
    fontFamily: 'Courier',
    color: '#fff',
  },
});
